/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Minus, X, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { formatDateToLocalInput, getClosestPoliceStation } from "@/lib/helper";
import apiService from "@/services/apiService";
import useErrorStore from "@/store/confirmationStore";
import { showValidationToast } from "@/lib/toastHelper";
import { validateDate, validateRequired } from "@/lib/validationHelper";
import { getUserInfo } from "@/services/userService";
interface GDFormPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: any;
  generalDiaryId?: string;
  onSuccess?: () => void;
}

type AnswerMap = Record<string, Record<string, string>>;
  type Question = {
    id: string;
    riskLevel?: "high" | "medium" | "low";
    subQuestions?: Question[];
  };
  type QuestionCategory = Question[];

const weaponTypes = ["Pistol", "Rifle", "Shotgun", "Revolver", "Knife", "Sword", "Dagger", "Stick/Lathi", "Iron Rod", "Axe", "Other Firearm", "Other Sharp Weapon", "Other Blunt Weapon", "Other"];

const weaponConditions = ["Good", "Fair", "Poor", "Damaged", "Broken", "Rusted", "Unknown"];

const policeStations = ["मुगलसराय", "अलीनगर", "बबुरी", "एन्ट ह्यूमन ट्रैफिकिंग यूनिट", "महिला थाना", "चन्दौली", "सैयदराजा", "कन्दवा", "सकलड़ीहा", "बलुआ", "धीना", "धानापुर", "चकिया", "शहाबगंज", "इलिया", "महिला पुलिस परामर्श केन्द्र चकिया", "नौगढ़", "चकरघट्टा"]

const districts = ["चन्दौली"]

const states = ["उत्तर प्रदेश"]

export function GDFormPopup({ open, onOpenChange, data, generalDiaryId, onSuccess }: GDFormPopupProps) {
  const showError = useErrorStore((s) => s.showError);
  const [formData, setFormData] = useState<any>({});
  const [caseCategories, setCaseCategories] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [incidents, setIncidents] = useState<any[]>([
    {
      incidentDescription: "",
      actionTaken: "",
      firstParty: [],
      secondParty: [],
      actsAndSections: [],
      selectedCategories: [],
      answers: {},
      riskLevel: "low"
    }
  ]);
  const [currentIncidentIndex, setCurrentIncidentIndex] = useState(0);
  const [newCriminal, setNewCriminal] = useState({
    fullName: "",
    fatherName: "",
    address: "",
    policeStation: "",
    remarks: "",
    cases: [],
  });
  const [newCase, setNewCase] = useState({
    caseType: "",
    firNumber: "",
    caseNumber: "",
    sectionOfLaw: "",
    dateRegistered: "",
    status: "PENDING",
    remarks: "",
  });
  const [criminalHistory, setCriminalHistory] = useState<
    {
      fullName: string;
      fatherName: string;
      address: string;
      policeStation: string;
      remarks: string;
      cases: any;
    }[]
  >([]);

  const addCriminalHistory = () => {
    if (!newCriminal.fullName || !newCriminal.address) return;

    let parsedCases = [];
    try {
      parsedCases = newCriminal.cases ? newCriminal.cases : [];
    } catch {
      alert("Invalid JSON in 'Cases'");
      return;
    }

    setCriminalHistory([
      ...criminalHistory,
      {
        ...newCriminal,
        cases: parsedCases,
      },
    ]);

    // Reset input
    setNewCriminal({
      fullName: "",
      fatherName: "",
      address: "",
      policeStation: "",
      remarks: "",
      cases: [],
    });
  };

  const removeCriminalHistory = (index: number) => {
    setCriminalHistory((prev) => prev.filter((_, i) => i !== index));
  };

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "file_name":
        return validateRequired(value, "File Name");
      case "gdNo":
        return validateRequired(value, "GD Number");
      case "gdDateTime":
        return validateDate(value, "GD Date & Time", { allowFuture: false });
      case "reportPrintedOn":
        return validateDate(value, "Report Printed On", { allowFuture: false });
      case "gdType":
        return validateRequired(value, "GD Type");
      case "state":
        return validateRequired(value, "State");
      case "districtUnit":
        return validateRequired(value, "District Unit");
      case "policeStation":
        return validateRequired(value, "Police Station");
      case "entryForOfficer":
        return validateRequired(value, "Entry For Officer");
      case "officerName":
        return validateRequired(value, "Officer Name");
      case "informationType":
        return validateRequired(value, "Information Type");
      case "underSection":
        return validateRequired(value, "Under Section");
      case "subject":
        return validateRequired(value, "Subject");
      default:
        return "";
    }
  };

  const getFieldError = (field: string): string => {
    return validationErrors[field] || "";
  };

  // Helper for array field errors (e.g., weapons[index].type)
  const getArrayFieldError = (arrayField: string, index: number, field: string): string => {
    if (validationErrors[arrayField] && Array.isArray(validationErrors[arrayField]) && validationErrors[arrayField][index] && validationErrors[arrayField][index][field]) {
      return validationErrors[arrayField][index][field];
    }
    return "";
  };

  const validateForm = (): boolean => {
    const errors: any = {};
    let hasErrors = false;
    const requiredFields = ["file_name", "gdNo", "gdDateTime", "gdType", "state", "districtUnit", "policeStation", "incidentVillage", "entryForOfficer", "officerName", "informationType", "subject", 'reportPrintedOn'];
    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
        hasErrors = true;
      }
    });
    // Validate that each incident has a category selected
    incidents.forEach((incident, index) => {
      if (!incident.selectedCategories || incident.selectedCategories.length === 0) {
        errors[`incident_${index}_category`] = `Incident ${index + 1}: Category must be selected`;
        hasErrors = true;
      }
    });
    // Validate police station against users police station (skip in edit mode for admins)
    if (!generalDiaryId) {
      const userInfo: any = getUserInfo();
      if (userInfo && Array.isArray(userInfo.policeStations)) {
        const assignedStations = userInfo.policeStations.map((ps: any) => ps.name);
        if (!assignedStations.includes(formData.policeStation)) {
          errors.policeStation = `Police Station must match one of your assigned stations (${assignedStations.join(', ')})`;
          hasErrors = true;
        }
      }
    }

    const questionsValid = validateQuestions();
    if (!questionsValid) {
      hasErrors = true;
    }

    setValidationErrors(errors);
    return !hasErrors;
  };

  const validateQuestions = (): boolean => {
    let hasUnansweredQuestions = false;
    const unansweredQuestions: string[] = [];

    // Validate questions for each incident
    incidents.forEach((incident, incidentIndex) => {
      incident.selectedCategories.forEach((category: any) => {
        const categoryAnswers = incident.answers[category.id] || {};

        category.questions.forEach((question: any) => {
          const answer = categoryAnswers[question.id];

          if (!answer || (answer !== "Yes" && answer !== "No")) {
            hasUnansweredQuestions = true;
            unansweredQuestions.push(`Incident ${incidentIndex + 1} - ${category.title}: ${question.questionText}`);
            return;
          }

          // If question is answered "Yes" and has sub-questions, validate sub-questions too
          if (answer === "Yes" && question.subQuestions?.length > 0) {
            question.subQuestions.forEach((subQuestion: any) => {
              const subAnswer = categoryAnswers[subQuestion.id];
              if (!subAnswer || (subAnswer !== "Yes" && subAnswer !== "No")) {
                hasUnansweredQuestions = true;
                unansweredQuestions.push(`Incident ${incidentIndex + 1} - ${category.title}: ${subQuestion.questionText}`);
              }
            });
          }
        });
      });
    });

    if (hasUnansweredQuestions) {
      showError(`Please answer all questions. Unanswered questions: ${unansweredQuestions.slice(0, 3).join(", ")}${unansweredQuestions.length > 3 ? "..." : ""}`);
      return false;
    }

    return true;
  };

  const ErrorMessage = ({ error }: { error: string }) =>
    error ? (
      <div className="flex items-center gap-1 text-red-600 text-sm mt-1">
        <span>{error}</span>
      </div>
    ) : null;

  useEffect(() => {
    const init = async () => {
      if (!data) return;

      const mappedData = mapOldDataToNewStructure(data);
      setFormData(mappedData);

      const parsedIncidents = parseIncidents(data);
      if (parsedIncidents.length > 0) {
        setIncidents(parsedIncidents);
        setCurrentIncidentIndex(0);
      }

      const fetchedCategories = await fetchCaseCategories();

      // Pre-populate category and answers when editing an existing GD
      if (data._existingCategoryId && fetchedCategories) {
        const matchedCat = (fetchedCategories as any[]).find((c: any) => c.id === data._existingCategoryId);
        if (matchedCat) {
          setIncidents(prev => {
            const updated = [...prev];
            updated[0] = {
              ...updated[0],
              selectedCategories: [matchedCat],
              answers: data._existingAnswers || {},
            };
            return updated;
          });
        }
      }
    };

    init();
  }, [data]);

  const parseIncidents = (oldData: any) => {
    const gd = oldData.general_diary || {};

    // Helper function to normalize party data
    const normalizeParty = (party: any) => ({
      name: party.Name || party.name || "",
      fatherName: party.Father_Name || party.fatherName || "",
      address: party.address || ""
    });

    // Helper function to normalize acts and sections
    const normalizeActsAndSections = (item: any) => ({
      act: item.Act || item.act || "",
      section: item.Section || item.section || ""
    });

    // Check if new multi-incident structure exists
    if (gd.Incidents && Array.isArray(gd.Incidents) && gd.Incidents.length > 0) {
      return gd.Incidents.map((incident: any) => ({
        incidentDescription: incident.Details?.Incident_Description || "",
        actionTaken: incident.Details?.Action_Taken || "",
        firstParty: (incident.Details?.First_Party || []).map(normalizeParty),
        secondParty: incident.Details?.Second_Party
          ? (Array.isArray(incident.Details.Second_Party)
              ? incident.Details.Second_Party.map(normalizeParty)
              : [normalizeParty(incident.Details.Second_Party)])
          : [],
        actsAndSections: (incident.Acts_And_Sections || []).map(normalizeActsAndSections),
        selectedCategories: [],
        answers: {},
        riskLevel: "low"
      }));
    }

    // Fall back to old single-incident structure
    return [{
      incidentDescription: gd.Details?.Incident_Description || "",
      actionTaken: gd.Details?.Action_Taken || "",
      firstParty: (gd.Details?.First_Party || gd.Details?.Suspects || []).map(normalizeParty),
      secondParty: gd.Details?.Second_Party
        ? (Array.isArray(gd.Details.Second_Party)
            ? gd.Details.Second_Party.map(normalizeParty)
            : [normalizeParty(gd.Details.Second_Party)])
        : (gd.Details?.Victim_or_Opposing_Party
            ? (Array.isArray(gd.Details.Victim_or_Opposing_Party)
                ? gd.Details.Victim_or_Opposing_Party.map(normalizeParty)
                : [normalizeParty(gd.Details.Victim_or_Opposing_Party)])
            : []),
      actsAndSections: (gd.Acts_And_Sections || []).map(normalizeActsAndSections),
      selectedCategories: [],
      answers: {},
      riskLevel: "low"
    }];
  };

  const mapOldDataToNewStructure = (oldData: any) => {
    const gd = oldData.general_diary || {};

    return {
      file_name: oldData.file_name || "",
      // Map old field names to new field names
      gdNo: gd.GD_No || "",
      gdDateTime: formatDateToLocalInput(gd.GD_DateTime) || "",
      gdType: gd.GD_TYPE || "",
      state: gd.State || "",
      districtUnit: gd.District_Unit || "",
      policeStation: gd.Police_Station || "",
      incidentVillage: gd.Incident_Village || "",
      entryForOfficer: gd.Entry_For_Officer || "",
      informationType: gd.Information_Type || "निवारक कार्रवाई",
      underSection: gd.Under_Section || "",
      officerName: gd.Officer_Name || "",
      caseType: gd.Case_Type || "",
      subject: gd.Subject || "",
      incidentDescription: gd.Details?.Incident_Description || "",
      actionTaken: gd.Details?.Action_Taken || "",
      riskLevel: gd.riskLevel || "low",
      additionalDetails: gd.additionalDetails || "",
      criminalHistory: criminalHistory ? criminalHistory : [],
      notes: gd.notes || "",
      reportPrintedOn: formatDateToLocalInput(gd.Report_Printed_On) || "",
      reportPrintedByName: gd.Report_Printed_By?.Name || "",
      reportPrintedByRank: gd.Report_Printed_By?.Rank || "",
      signatureName: gd.Signature?.Name || "",
      signatureRank: gd.Signature?.Rank || "",
      signatureIdNo: gd.Signature?.ID_No || "",
      // Keep arrays as they are for now
      suspects: gd.Details?.Suspects || [],
      victims: gd.Details?.Victims || (gd.Details?.Victim_or_Opposing_Party ? [gd.Details.Victim_or_Opposing_Party] : []),
      actsAndSections: gd.Acts_And_Sections || [],
      weapons: gd.weapons || [],
      // Categories will be handled separately
      selectedCategoryIds: oldData.caseCategory || [],
    };
  };

  const fetchCaseCategories = async () => {
    try {
      const categories:any = await apiService.get('case-category');
      setCaseCategories(categories);
      return categories;
    } catch (error:any) {
      showError(error);
    }
  };

  const handleInputChange = (field: string, value: string | boolean | number | any[]) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const removeCase = (index: number) => {
    setNewCriminal((prev) => ({
      ...prev,
      cases: prev.cases.filter((_, i) => i !== index),
    }));
  };

  // Incident Management Functions
  const addIncident = () => {
    setIncidents([
      ...incidents,
      {
        incidentDescription: "",
        actionTaken: "",
        firstParty: [],
        secondParty: [],
        actsAndSections: [],
        selectedCategories: [],
        answers: {}
      }
    ]);
    setCurrentIncidentIndex(incidents.length);
  };

  const removeIncident = (index: number) => {
    if (incidents.length === 1) return; // Keep at least one incident
    const newIncidents = incidents.filter((_, i) => i !== index);
    setIncidents(newIncidents);
    setCurrentIncidentIndex(Math.max(0, Math.min(index, newIncidents.length - 1)));
  };

  const updateIncidentField = (field: string, value: string) => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex] = {
      ...newIncidents[currentIncidentIndex],
      [field]: value
    };
    setIncidents(newIncidents);
  };

  // First Party Management
  const addFirstParty = () => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].firstParty.push({
      name: "",
      fatherName: "",
      address: ""
    });
    setIncidents(newIncidents);
  };

  const removeFirstParty = (index: number) => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].firstParty = newIncidents[currentIncidentIndex].firstParty.filter((_: any, i: number) => i !== index);
    setIncidents(newIncidents);
  };

  const updateFirstParty = (index: number, field: string, value: string) => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].firstParty[index] = {
      ...newIncidents[currentIncidentIndex].firstParty[index],
      [field]: value
    };
    setIncidents(newIncidents);
  };

  // Second Party Management
  const addSecondParty = () => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].secondParty.push({
      name: "",
      fatherName: "",
      address: ""
    });
    setIncidents(newIncidents);
  };

  const removeSecondParty = (index: number) => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].secondParty = newIncidents[currentIncidentIndex].secondParty.filter((_: any, i: number) => i !== index);
    setIncidents(newIncidents);
  };

  const updateSecondParty = (index: number, field: string, value: string) => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].secondParty[index] = {
      ...newIncidents[currentIncidentIndex].secondParty[index],
      [field]: value
    };
    setIncidents(newIncidents);
  };

  // Incident Acts & Sections Management
  const addIncidentActSection = () => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].actsAndSections.push({
      act: "",
      section: ""
    });
    setIncidents(newIncidents);
  };

  const removeIncidentActSection = (index: number) => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].actsAndSections = newIncidents[currentIncidentIndex].actsAndSections.filter((_: any, i: number) => i !== index);
    setIncidents(newIncidents);
  };

  const updateIncidentActSection = (index: number, field: string, value: string) => {
    const newIncidents = [...incidents];
    newIncidents[currentIncidentIndex].actsAndSections[index] = {
      ...newIncidents[currentIncidentIndex].actsAndSections[index],
      [field]: value
    };
    setIncidents(newIncidents);
  };

  // Incident Category Management (Single Select)
  const handleIncidentCategoryChange = (categoryId: number) => {
    const newIncidents = [...incidents];
    const currentIncident = newIncidents[currentIncidentIndex];

    const categoryToSelect = caseCategories.find((cat: any) => cat.id === categoryId);
    if (categoryToSelect) {
      // Replace with single category (not array)
      currentIncident.selectedCategories = [categoryToSelect];
      // Clear all answers when changing category
      currentIncident.answers = {};
    }

    setIncidents(newIncidents);
  };

  const handleIncidentQuestionAnswer = async (categoryId: number, question: any, answer: string) => {
    const newIncidents = [...incidents];
    const currentIncident = newIncidents[currentIncidentIndex];

    // Store answers in nested format: answers[categoryId][questionId]
    if (!currentIncident.answers[categoryId]) {
      currentIncident.answers[categoryId] = {};
    }
    currentIncident.answers[categoryId][question.id] = answer;

    // Calculate and update risk level for this incident
    const riskLevel = await analyzeIncidentRisk(currentIncident.answers, caseCategories.map((item: any) => item.questions));
    currentIncident.riskLevel = riskLevel;

    setIncidents(newIncidents);
  };

  const addWeapon = async () => {
    const currentWeapons = formData.weapons || [];
    handleInputChange("weapons", [
      ...currentWeapons,
      {
        type: "",
        model: "",
        serialNumber: "",
        licenseNumber: "",
        recoveredFrom: "",
        condition: "",
        remarks: "",
        isSeized: false,
      },
    ]);
    handleInputChange("riskLevel", "high"); // rule #0: any weapon → high risk
  };

  const removeWeapon = async (index: number) => {
    const currentWeapons = formData.weapons || [];
    handleInputChange(
      "weapons",
      currentWeapons.filter((_: any, i: number) => i !== index)
    );
    // Risk level is now calculated per-incident, no need to update global risk
    // Weapons are global, so this doesn't affect per-incident risk calculation
  };

  const updateWeapon = (index: number, field: string, value: string | boolean) => {
    const currentWeapons = [...(formData.weapons || [])];
    currentWeapons[index] = { ...currentWeapons[index], [field]: value };
    handleInputChange("weapons", currentWeapons);
  };

  // Analyze risk for a specific incident
  const analyzeIncidentRisk = async (incidentAnswers: AnswerMap, questions: QuestionCategory[]) => {
    // Weapons are global, so we don't check them here - risk is per-incident based on answers only
    let finalRisk: "low" | "medium" | "high" = "low";
    const allQuestions: Record<string, Question> = {};
    questions.flat().forEach((q) => {
      allQuestions[q.id] = q;
      if (q.subQuestions?.length) {
        q.subQuestions.forEach((sq) => (allQuestions[sq.id] = sq));
      }
    });
    for (const [, categoryAnswers] of Object.entries(incidentAnswers)) {
      for (const [questionId, ans] of Object.entries(categoryAnswers)) {
        if (ans === "Yes") {
          const q = allQuestions[questionId];
          if (q?.riskLevel === "high") return "high"; // rule #1: any high → stop immediately
          if (q?.riskLevel === "medium") finalRisk = "medium"; // rule #2: track medium
        }
      }
    }
    return finalRisk; // if no high, maybe medium, else low
  };

  const QuestionRow = ({
  question,
  categoryId,
  isSubQuestion = false,
  answers,
  handleQuestionAnswer,
}: {
  question: any;
  categoryId: any;
  isSubQuestion?: boolean;
  answers: any;
  handleQuestionAnswer: (categoryId: any, selectedQuestion: any, answer: any) => void;
}) => {
  const answer = answers?.[categoryId]?.[question.id];
  const showSubQuestions = answer === "Yes" && question.subQuestions;
  const isAnswered = answer === "Yes" || answer === "No";

  return (
    <div className="mb-1">
      <div
        className={`flex items-center justify-between py-1.5 px-2 ${
          isSubQuestion
            ? "bg-blue-50 border-l-2 border-blue-300 ml-4"
            : "bg-gray-50"
        } rounded ${!isAnswered && showErrors ? "border border-red-300" : ""}`}
      >
        <div className="flex items-center space-x-2 flex-1">
          {isSubQuestion && <span className="text-blue-600 text-xs">↳</span>}
          <span
            className={`${
              isSubQuestion
                ? "text-xs text-gray-700"
                : "text-sm font-medium text-gray-800"
            }`}
          >
            {question.questionText}
          </span>
        </div>

        {/* Compact Yes/No Buttons */}
        <div className="flex space-x-1">
          <button
            onClick={() =>
              handleQuestionAnswer(categoryId, question, "Yes")
            }
            className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-all ${
              answer === "Yes"
                ? "bg-green-500 text-white shadow-sm"
                : "bg-gray-200 hover:bg-green-100 text-gray-600 hover:text-green-700"
            }`}
          >
            <CheckCircle className="w-3 h-3" />
            <span>हाँ</span>
          </button>

          <button
            onClick={() => handleQuestionAnswer(categoryId, question, "No")}
            className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-all ${
              answer === "No"
                ? "bg-red-500 text-white shadow-sm"
                : "bg-gray-200 hover:bg-red-100 text-gray-600 hover:text-red-700"
            }`}
          >
            <XCircle className="w-3 h-3" />
            <span>नहीं</span>
          </button>
        </div>
      </div>

      {/* Sub-questions */}
      {showSubQuestions && (
        <div className="mt-1">
          {question.subQuestions.map((subQuestion: any) => (
            <QuestionRow
              key={subQuestion.id}
              question={subQuestion}
              categoryId={categoryId}
              isSubQuestion={true}
              answers={answers}
              handleQuestionAnswer={handleQuestionAnswer}
            />
          ))}
        </div>
      )}
    </div>
  );
};


  const buildIncidentPayload = (incident: any) => ({
    Details: {
      Incident_Description: incident.incidentDescription,
      Action_Taken: incident.actionTaken,
      First_Party: incident.firstParty.map((party: any) => ({
        Name: party.name || party.Name,
        Father_Name: party.fatherName || party.Father_Name,
        address: party.address || "",
      })),
      Second_Party: incident.secondParty.length === 1
        ? { Name: incident.secondParty[0].name || incident.secondParty[0].Name, Father_Name: incident.secondParty[0].fatherName || incident.secondParty[0].Father_Name, address: incident.secondParty[0].address || "" }
        : incident.secondParty.map((party: any) => ({ Name: party.name || party.Name, Father_Name: party.fatherName || party.Father_Name, address: party.address || "" })),
    },
    Acts_And_Sections: incident.actsAndSections.map((item: any) => ({ Act: item.act || item.Act, Section: item.section || item.Section })),
    CategoryIds: incident.selectedCategories.map((cat: any) => cat.id),
    CategoryAnswers: transformAnswers(incident.answers),
    RiskLevel: incident.riskLevel || "low",
  });

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!validateForm()) {
      showValidationToast(validationErrors);
      return;
    }
    const userInfo:any = getUserInfo();
    const payload = { ...formData };
    if (userInfo) payload["createdById"] = userInfo.id;
    const policeStation:any = getClosestPoliceStation(payload.policeStation);

    let requestPayload: any;

    if (generalDiaryId) {
      // ── EDIT MODE: compute diff, send only changed fields ──────────────
      const origGD = data.general_diary || {};
      const origMapped = mapOldDataToNewStructure(data);
      const cur = incidents[0] || {};

      // Scalar fields changed
      const scalarFields = [
        'gdNo', 'gdType', 'state', 'districtUnit', 'incidentVillage',
        'entryForOfficer', 'informationType', 'underSection', 'officerName',
        'caseType', 'subject', 'riskLevel', 'additionalDetails', 'notes',
        'reportPrintedOn', 'reportPrintedByName', 'reportPrintedByRank',
        'signatureName', 'signatureRank', 'signatureIdNo',
      ] as const;
      const changedScalars: Record<string, any> = {};
      for (const field of scalarFields) {
        if (formData[field] !== origMapped[field]) changedScalars[field] = formData[field];
      }
      if (formData.gdDateTime !== origMapped.gdDateTime) changedScalars.gdDateTime = formData.gdDateTime;
      if (formData.file_name !== origMapped.file_name) changedScalars.fileName = formData.file_name;
      if (policeStation.name !== (origGD.Police_Station || "")) changedScalars.policeStation = policeStation.name;

      // Incident-level sections changed
      const origFirstParty: any[] = Array.isArray(origGD.Details?.First_Party) ? origGD.Details.First_Party : [];
      const origSecondPartyRaw = origGD.Details?.Second_Party;
      const origSecondParty: any[] = origSecondPartyRaw
        ? (Array.isArray(origSecondPartyRaw) ? origSecondPartyRaw : [origSecondPartyRaw])
        : [];
      const origActsAndSections: any[] = Array.isArray(origGD.Acts_And_Sections) ? origGD.Acts_And_Sections : [];
      const origCategoryId = data._existingCategoryId;
      const origAnswers = data._existingAnswers || {};

      const curFirstParty = cur.firstParty?.map((p: any) => ({ Name: p.name || p.Name, Father_Name: p.fatherName || p.Father_Name, address: p.address || "" })) || [];
      const curSecondParty = (cur.secondParty || []).map((p: any) => ({ Name: p.name || p.Name, Father_Name: p.fatherName || p.Father_Name, address: p.address || "" }));
      const curActsAndSections = (cur.actsAndSections || []).map((a: any) => ({ Act: a.act || a.Act, Section: a.section || a.Section }));
      const curCategoryId = cur.selectedCategories?.[0]?.id;
      const curAnswers = cur.answers || {};

      const suspectsChanged = JSON.stringify(curFirstParty) !== JSON.stringify(origFirstParty);
      const victimsChanged = JSON.stringify(curSecondParty) !== JSON.stringify(origSecondParty);
      const actsChanged = JSON.stringify(curActsAndSections) !== JSON.stringify(origActsAndSections);
      const categoryChanged = curCategoryId !== origCategoryId || JSON.stringify(curAnswers) !== JSON.stringify(origAnswers);
      const incDescChanged = (cur.incidentDescription || "") !== (origGD.Details?.Incident_Description || "");
      const actionTakenChanged = (cur.actionTaken || "") !== (origGD.Details?.Action_Taken || "");

      // Build incident patch (only changed sections)
      const incidentPatch: any = { RiskLevel: cur.riskLevel || "low", Details: {} };
      if (suspectsChanged) incidentPatch.Details.First_Party = curFirstParty;
      if (victimsChanged) incidentPatch.Details.Second_Party = curSecondParty;
      if (incDescChanged) incidentPatch.Details.Incident_Description = cur.incidentDescription;
      if (actionTakenChanged) incidentPatch.Details.Action_Taken = cur.actionTaken;
      if (actsChanged) incidentPatch.Acts_And_Sections = curActsAndSections;
      if (categoryChanged) {
        incidentPatch.CategoryIds = cur.selectedCategories?.map((c: any) => c.id) || [];
        incidentPatch.CategoryAnswers = transformAnswers(curAnswers);
      }

      requestPayload = {
        documentId: data.id,
        createdById: payload.createdById,
        ...changedScalars,
        incidents: [incidentPatch],
      };
    } else {
      // ── CREATE MODE: send full payload ──────────────────────────────────
      const transformedIncidents = incidents.map(buildIncidentPayload);
      requestPayload = {
        documentId: data.id,
        fileName: payload.file_name,
        gdNo: payload.gdNo,
        gdDateTime: payload.gdDateTime,
        gdType: payload.gdType,
        state: payload.state,
        districtUnit: payload.districtUnit,
        policeStation: policeStation.name,
        entryForOfficer: payload.entryForOfficer,
        informationType: payload.informationType,
        underSection: payload.underSection,
        officerName: payload.officerName,
        caseType: payload.caseType,
        subject: payload.subject,
        incidentDescription: payload.incidentDescription,
        actionTaken: payload.actionTaken,
        riskLevel: payload.riskLevel,
        additionalDetails: payload.additionalDetails,
        criminalHistory: criminalHistory,
        notes: payload.notes,
        reportPrintedOn: payload.reportPrintedOn,
        reportPrintedByName: payload.reportPrintedByName,
        reportPrintedByRank: payload.reportPrintedByRank,
        signatureName: payload.signatureName,
        signatureRank: payload.signatureRank,
        signatureIdNo: payload.signatureIdNo,
        suspects: payload.suspects?.map((s: any) => ({ name: s.name || s.Name, fatherName: s.fatherName || s.Father_Name, address: s.address || "" })) || [],
        victims: payload.victims?.map((v: any) => ({ name: v.name || v.Name, fatherName: v.fatherName || v.Father_Name, address: v.address || "", type: v.type || v.Type || "VICTIM" })) || [],
        actsAndSections: payload.actsAndSections?.map((a: any) => ({ act: a.act || a.Act, section: a.section || a.Section })) || [],
        weapons: payload.weapons || [],
        categoryIds: payload.selectedCategoryIds || [],
        incidents: transformedIncidents,
        categoryAnswers: [],
        createdById: payload.createdById,
      };
    }

    try {
      const response = generalDiaryId
        ? await apiService.put(`general-diaries/${generalDiaryId}`, JSON.stringify(requestPayload))
        : await apiService.post('general-diaries', JSON.stringify(requestPayload));
      if (response) {
        toast.success("General Diary saved successfully!", {
          description: "Your General Diary has been saved.",
          action: { label: "Ok", onClick: () => console.log("Ok") },
        });
        setIsPreviewMode(false);
        onOpenChange(false);
        onSuccess?.();
      } else {
        showError("Failed to save General Diary. Please try again.");
      }
    } catch (error:any) {
      showError(error);
    }
  };

  const transformAnswers = (oldPayload: Record<string, Record<string, string>>) => {
    return Object.entries(oldPayload).map(([categoryId, questions]) => ({
      categoryId:Number(categoryId),
      answers: Object.entries(questions).map(([questionId, answer]) => ({
        questionId,
        answer,
      })),
    }));
  }

  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[96vh] bg-gray-50 flex flex-col w-[96%] p-0 gap-0" style={{ maxWidth: "1900px" }}>
        <DialogHeader className="flex-shrink-0 px-6 py-4 bg-white border-b">
          <DialogTitle className="text-lg font-semibold text-gray-900">General Diary Entry Form</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6 min-h-0">
          <div className="space-y-4 py-5">
            {/* File Name */}
            <div className="space-y-1">
              <Label htmlFor="file_name" className="text-sm font-medium text-black">
                File Name
              </Label>
              <Input disabled={isPreviewMode} id="file_name" value={formData.file_name || ""} onChange={(e) => handleInputChange("file_name", e.target.value)} className={showErrors && getFieldError("file_name") ? "border-red-500" : "border-gray-300"} />
              {showErrors && <ErrorMessage error={getFieldError("file_name")} />}
            </div>

            <Separator />

            {/* Basic Information */}
            <Card className="border-0 bg-white shadow-sm">
              <CardHeader className="pb-3 pt-4 px-5">
                <CardTitle className="text-sm font-medium text-gray-700">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 px-5 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-600">GD Number</Label>
                    <Input disabled={isPreviewMode} value={formData.gdNo || ""} onChange={(e) => handleInputChange("gdNo", e.target.value)} className={showErrors && getFieldError("gdNo") ? "border-red-500" : "border-gray-300"} />
                    {showErrors && <ErrorMessage error={getFieldError("gdNo")} />}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">GD Date & Time</Label>
                    <Input disabled={isPreviewMode} type="datetime-local" value={formData.gdDateTime || ""} onChange={(e) => handleInputChange("gdDateTime", e.target.value)} className={showErrors && getFieldError("gdDateTime") ? "border-red-500" : "border-gray-300"} />
                    {showErrors && <ErrorMessage error={getFieldError("gdDateTime")} />}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">GD Type</Label>
                    <Input disabled={isPreviewMode} value={formData.gdType || ""} onChange={(e) => handleInputChange("gdType", e.target.value)} className={showErrors && getFieldError("gdType") ? "border-red-500" : "border-gray-300"} />
                    {showErrors && <ErrorMessage error={getFieldError("gdType")} />}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">State</Label>
                    <Select disabled={isPreviewMode} value={formData.state || ""} onValueChange={(value) => handleInputChange("state", value)}>
                      <SelectTrigger className={showErrors && getFieldError("districtUnit") ? "border-red-500 w-full" : "border-gray-300 w-full"}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200">
                        {states && states.map((st:any, index:number) => (
                          <SelectItem value={st} key={st+index}>{st}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {showErrors && <ErrorMessage error={getFieldError("state")} />}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">District Unit</Label>
                    <Select disabled={isPreviewMode} value={formData.districtUnit || ""} onValueChange={(value) => handleInputChange("districtUnit", value)}>
                      <SelectTrigger className={showErrors && getFieldError("districtUnit") ? "border-red-500 w-full" : "border-gray-300 w-full"}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200">
                        {districts && districts.map((ds:any) => (
                          <SelectItem value={ds} key={'ds'+ds}>{ds}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {showErrors && <ErrorMessage error={getFieldError("districtUnit")} />}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Police Station</Label>
                    <Select disabled={isPreviewMode} value={formData.policeStation || ""} onValueChange={(value) => handleInputChange("policeStation", value)}>
                      <SelectTrigger className={showErrors && getFieldError("policeStation") ? "border-red-500 w-full" : "border-gray-300 w-full"}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200">
                        {policeStations && policeStations.map((ps:any, index:number) => (
                          <SelectItem value={ps} key={'ps'+index+ps}>{ps}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {showErrors && <ErrorMessage error={getFieldError("policeStation")} />}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Incident Village</Label>
                    <Input disabled={isPreviewMode} value={formData.incidentVillage || ""} onChange={(e) => handleInputChange("incidentVillage", e.target.value)} className={showErrors && getFieldError("incidentVillage") ? "border-red-500" : "border-gray-300"} />
                    {showErrors && <ErrorMessage error={getFieldError("incidentVillage")} />}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Officer Information */}
            <Card className="border-0 bg-white shadow-sm">
              <CardHeader className="pb-3 pt-4 px-5">
                <CardTitle className="text-sm font-medium text-gray-700">Officer Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 px-5 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Entry For Officer</Label>
                    <Input disabled={isPreviewMode} value={formData.entryForOfficer || ""} onChange={(e) => handleInputChange("entryForOfficer", e.target.value)} className={showErrors && getFieldError("entryForOfficer") ? "border-red-500" : "border-gray-300"} />
                    {showErrors && <ErrorMessage error={getFieldError("entryForOfficer")} />}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Officer Name</Label>
                    <Input disabled={isPreviewMode} value={formData.officerName || ""} onChange={(e) => handleInputChange("officerName", e.target.value)} className={showErrors && getFieldError("officerName") ? "border-red-500" : "border-gray-300"} />
                    {showErrors && <ErrorMessage error={getFieldError("officerName")} />}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Information Type</Label>
                    <Input disabled={isPreviewMode} value={formData.informationType || ""} onChange={(e) => handleInputChange("informationType", e.target.value)} className={showErrors && getFieldError("informationType") ? "border-red-500" : "border-gray-300"} />
                    {showErrors && <ErrorMessage error={getFieldError("informationType")} />}
                  </div>
                </div>
                <div className="space-y-1 hidden">
                  <Label className="text-xs font-medium text-gray-600">Under Section</Label>
                  <Input disabled={isPreviewMode} value={formData.underSection || ""} onChange={(e) => handleInputChange("underSection", e.target.value)} className={showErrors && getFieldError("underSection") ? "border-red-500" : "border-gray-300"} />
                  {showErrors && <ErrorMessage error={getFieldError("underSection")} />}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-600">Subject</Label>
                  <Textarea disabled={isPreviewMode} value={formData.subject || ""} onChange={(e) => handleInputChange("subject", e.target.value)} className={showErrors && getFieldError("subject") ? "border-red-500" : "border-gray-300"} rows={2} />
                  {showErrors && <ErrorMessage error={getFieldError("subject")} />}
                </div>
              </CardContent>
            </Card>

            {/* Case Details & Incidents */}
            <Card className="border-0 bg-white shadow-sm">
              <CardHeader className="pb-3 pt-4 px-5">
                <CardTitle className="text-sm font-medium text-gray-700">Case Details & Incidents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 px-5 pb-4">
                {/* Incident Tabs */}
                <div className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                  <div className="flex flex-wrap gap-1.5">
                    {incidents.map((_, idx) => (
                      <Button
                        key={idx}
                        onClick={() => setCurrentIncidentIndex(idx)}
                        variant={currentIncidentIndex === idx ? "default" : "outline"}
                        size="sm"
                        className={`h-7 px-3 text-xs ${currentIncidentIndex === idx ? "bg-[#0d2846] text-white" : ""}`}
                      >
                        Incident {idx + 1}
                        {incidents.length > 1 && currentIncidentIndex === idx && (
                          <X
                            className="h-3 w-3 ml-1.5 hover:text-red-400"
                            onClick={(e) => { e.stopPropagation(); removeIncident(idx); }}
                          />
                        )}
                      </Button>
                    ))}
                  </div>
                  <Button onClick={addIncident} size="sm" variant="outline" className="h-7 px-2 text-xs">
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>

                {/* 3-Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  {/* First Party */}
                  <div className="border border-blue-200 bg-blue-50/30 rounded p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-blue-900">First Party (प्रथम पक्ष)</h4>
                      <Button onClick={addFirstParty} size="sm" variant="ghost" className="h-6 w-6 p-0 text-blue-700">
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {incidents[currentIncidentIndex]?.firstParty.map((party: any, idx: number) => (
                        <div key={idx} className="bg-white p-2 rounded border border-blue-100 space-y-1.5">
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] font-medium text-blue-800">#{idx + 1}</span>
                            <Button onClick={() => removeFirstParty(idx)} size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-600">
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <Input
                            placeholder="Name"
                            value={party.name || ""}
                            onChange={(e) => updateFirstParty(idx, "name", e.target.value)}
                            className="h-7 text-xs"
                          />
                          <Input
                            placeholder="Father's Name"
                            value={party.fatherName || ""}
                            onChange={(e) => updateFirstParty(idx, "fatherName", e.target.value)}
                            className="h-7 text-xs"
                          />
                          <Textarea
                            placeholder="Address"
                            value={party.address || ""}
                            onChange={(e) => updateFirstParty(idx, "address", e.target.value)}
                            className="text-xs"
                            rows={2}
                          />
                        </div>
                      ))}
                      {incidents[currentIncidentIndex]?.firstParty.length === 0 && (
                        <p className="text-xs text-gray-500 text-center py-4">No first party added</p>
                      )}
                    </div>
                  </div>

                  {/* Second Party */}
                  <div className="border border-green-200 bg-green-50/30 rounded p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-green-900">Second Party (द्वितीय पक्ष)</h4>
                      <Button onClick={addSecondParty} size="sm" variant="ghost" className="h-6 w-6 p-0 text-green-700">
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {incidents[currentIncidentIndex]?.secondParty.map((party: any, idx: number) => (
                        <div key={idx} className="bg-white p-2 rounded border border-green-100 space-y-1.5">
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] font-medium text-green-800">#{idx + 1}</span>
                            <Button onClick={() => removeSecondParty(idx)} size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-600">
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <Input
                            placeholder="Name"
                            value={party.name || ""}
                            onChange={(e) => updateSecondParty(idx, "name", e.target.value)}
                            className="h-7 text-xs"
                          />
                          <Input
                            placeholder="Father's Name"
                            value={party.fatherName || ""}
                            onChange={(e) => updateSecondParty(idx, "fatherName", e.target.value)}
                            className="h-7 text-xs"
                          />
                          <Textarea
                            placeholder="Address"
                            value={party.address || ""}
                            onChange={(e) => updateSecondParty(idx, "address", e.target.value)}
                            className="text-xs"
                            rows={2}
                          />
                        </div>
                      ))}
                      {incidents[currentIncidentIndex]?.secondParty.length === 0 && (
                        <p className="text-xs text-gray-500 text-center py-4">No second party added</p>
                      )}
                    </div>
                  </div>

                  {/* Acts & Sections */}
                  <div className="border border-orange-200 bg-orange-50/30 rounded p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-orange-900">Acts & Sections</h4>
                      <Button onClick={addIncidentActSection} size="sm" variant="ghost" className="h-6 w-6 p-0 text-orange-700">
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {incidents[currentIncidentIndex]?.actsAndSections.map((item: any, idx: number) => (
                        <div key={idx} className="bg-white p-2 rounded border border-orange-100 space-y-1.5">
                          <div className="flex justify-between items-center">
                            <span className="text-[10px] font-medium text-orange-800">#{idx + 1}</span>
                            <Button onClick={() => removeIncidentActSection(idx)} size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-600">
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <Input
                            placeholder="Act"
                            value={item.act || ""}
                            onChange={(e) => updateIncidentActSection(idx, "act", e.target.value)}
                            className="h-7 text-xs"
                          />
                          <Input
                            placeholder="Section"
                            value={item.section || ""}
                            onChange={(e) => updateIncidentActSection(idx, "section", e.target.value)}
                            className="h-7 text-xs"
                          />
                        </div>
                      ))}
                      {incidents[currentIncidentIndex]?.actsAndSections.length === 0 && (
                        <p className="text-xs text-gray-500 text-center py-4">No acts/sections added</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Case Categories for this Incident */}
                <div className="border-t pt-3 mt-2">
                  <h4 className="text-xs font-semibold text-gray-800 mb-2">Case Category (Select One)</h4>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg p-2">
                    <RadioGroup
                      value={incidents[currentIncidentIndex]?.selectedCategories[0]?.id?.toString() || ""}
                      onValueChange={(value:string) => handleIncidentCategoryChange(Number(value))}
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        {caseCategories.map((category:any) => {
                          const isSelected = incidents[currentIncidentIndex]?.selectedCategories.some((cat: any) => cat.id === category.id);
                          return (
                            <div key={category.id} className={`border-2 rounded-lg transition-all duration-200 ${
                              isSelected ? 'border-blue-500 bg-white' : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}>
                              {/* Category Header */}
                              <div className="p-2 border-b border-gray-100">
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem
                                    value={category.id.toString()}
                                    id={`incident-category-${currentIncidentIndex}-${category.id}`}
                                  />
                                  <Label
                                    htmlFor={`incident-category-${currentIncidentIndex}-${category.id}`}
                                    className="text-xs font-medium cursor-pointer flex items-center space-x-1 flex-1"
                                  >
                                    <span className="text-sm">{category.icon}</span>
                                    <span>{category.title}</span>
                                  </Label>
                                </div>
                              </div>

                              {/* Integrated questions */}
                              {isSelected && (
                                <div className="p-2 max-h-64 overflow-y-auto">
                                  <div className="space-y-1">
                                    {category.questions.map((question:any) => (
                                      <QuestionRow
                                        key={question.id}
                                        question={question}
                                        categoryId={category.id}
                                        answers={incidents[currentIncidentIndex]?.answers || {}}
                                        isSubQuestion={question.parentQuestionId}
                                        handleQuestionAnswer={handleIncidentQuestionAnswer}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Suggested Sections and Risk Level */}
                  {incidents[currentIncidentIndex]?.selectedCategories.length > 0 && (
                    <div className="space-y-2 mt-2">
                      <div className="flex gap-3">
                        <div className="flex-1 space-y-1">
                          <Label className="text-[10px] font-medium text-gray-600">Suggested IPC Sections</Label>
                          <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto border border-gray-100 rounded p-1">
                            {incidents[currentIncidentIndex]?.selectedCategories
                              .flatMap((category: any) => (Array.isArray(category.ipcSections) ? category.ipcSections : []))
                              .filter((section: string, index: number, arr: string[]) => arr.indexOf(section) === index)
                              .map((section: string) => (
                                <Badge key={section} variant="secondary" className="bg-gray-100 text-gray-800 text-[10px] px-1 py-0">
                                  {section}
                                </Badge>
                              ))}
                          </div>
                        </div>
                        <div className="flex-1 space-y-1">
                          <Label className="text-[10px] font-medium text-gray-600">Suggested BNS Sections</Label>
                          <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto border border-gray-100 rounded p-1">
                            {incidents[currentIncidentIndex]?.selectedCategories
                              .flatMap((category: any) => (Array.isArray(category.bnsSections) ? category.bnsSections : []))
                              .filter((section: string, index: number, arr: string[]) => arr.indexOf(section) === index)
                              .map((section: string) => (
                                <Badge key={section} variant="secondary" className="bg-gray-100 text-gray-800 text-[10px] px-1 py-0">
                                  {section}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      </div>

                      {/* Risk Level Display */}
                      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                        <Label className="text-xs font-medium text-gray-600">Risk Level:</Label>
                        <Badge
                          className={`text-xs font-semibold ${
                            incidents[currentIncidentIndex]?.riskLevel === "high"
                              ? "bg-red-100 text-red-800 border-red-300"
                              : incidents[currentIncidentIndex]?.riskLevel === "medium"
                              ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                              : "bg-green-100 text-green-800 border-green-300"
                          }`}
                          variant="outline"
                        >
                          {incidents[currentIncidentIndex]?.riskLevel?.toUpperCase() || "LOW"}
                        </Badge>
                        <span className="text-[10px] text-gray-500 ml-auto">
                          Auto-calculated based on answers
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Incident Description & Action */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 pt-2">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Incident Description</Label>
                    <Textarea
                      disabled={isPreviewMode}
                      value={incidents[currentIncidentIndex]?.incidentDescription || ""}
                      onChange={(e) => updateIncidentField("incidentDescription", e.target.value)}
                      className="border-gray-300 text-xs"
                      rows={3}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Action Taken</Label>
                    <Textarea
                      disabled={isPreviewMode}
                      value={incidents[currentIncidentIndex]?.actionTaken || ""}
                      onChange={(e) => updateIncidentField("actionTaken", e.target.value)}
                      className="border-gray-300 text-xs"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Additional Details & Notes */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Additional Details</Label>
                    <Textarea disabled={isPreviewMode} value={formData.additionalDetails || ""} onChange={(e) => handleInputChange("additionalDetails", e.target.value)} className="border-gray-300 text-xs" rows={2} placeholder="Any additional information about the case..." />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Notes</Label>
                    <Textarea disabled={isPreviewMode} value={formData.notes || ""} onChange={(e) => handleInputChange("notes", e.target.value)} className="border-gray-300 text-xs" rows={2} placeholder="Internal notes and observations..." />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Weapons Section */}
            <Card className="border-0 bg-white shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-sm font-medium text-gray-700">Weapons & Exhibits</CardTitle>
                <Button onClick={addWeapon} size="sm" className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Weapon
                </Button>
              </CardHeader>
              <CardContent className="space-y-3 px-5 pb-4">
                {(formData.weapons || []).map((weapon: any, index: number) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-black">Weapon/Exhibit {index + 1}</h4>
                      <Button onClick={() => removeWeapon(index)} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                        <Minus className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Weapon Type</Label>
                        <Select disabled={isPreviewMode} value={weapon.type || ""} onValueChange={(value) => updateWeapon(index, "type", value)}>
                          <SelectTrigger className={showErrors && getArrayFieldError("weapons", index, "type") ? "border-red-500" : "border-gray-300"}>
                            <SelectValue placeholder="Select weapon type" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border border-gray-200">
                            {weaponTypes.map((type) => (
                              <SelectItem key={type} value={type} className="hover:bg-gray-50">
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {showErrors && <ErrorMessage error={getArrayFieldError("weapons", index, "type")} />}
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Model/Make</Label>
                        <Input disabled={isPreviewMode} value={weapon.model || ""} onChange={(e) => updateWeapon(index, "model", e.target.value)} className="border-gray-300" placeholder="Enter model/make" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Serial Number</Label>
                        <Input disabled={isPreviewMode} value={weapon.serialNumber || ""} onChange={(e) => updateWeapon(index, "serialNumber", e.target.value)} className="border-gray-300" placeholder="Enter serial number" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">License Number</Label>
                        <Input disabled={isPreviewMode} value={weapon.licenseNumber || ""} onChange={(e) => updateWeapon(index, "licenseNumber", e.target.value)} className="border-gray-300" placeholder="Enter license number" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Recovered From</Label>
                        <Input disabled={isPreviewMode} value={weapon.recoveredFrom || ""} onChange={(e) => updateWeapon(index, "recoveredFrom", e.target.value)} className="border-gray-300" placeholder="Person/Place name" />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Condition</Label>
                        <Select disabled={isPreviewMode} value={weapon.condition || ""} onValueChange={(value) => updateWeapon(index, "condition", value)}>
                          <SelectTrigger className="border-gray-300">
                            <SelectValue placeholder="Select condition" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border border-gray-200">
                            {weaponConditions.map((condition) => (
                              <SelectItem key={condition} value={condition} className="hover:bg-gray-50">
                                {condition}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="mt-3 space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox disabled={isPreviewMode} id={`weapon-seized-${index}`} checked={weapon.isSeized || false} onCheckedChange={(checked) => updateWeapon(index, "isSeized", checked)} />
                        <Label htmlFor={`weapon-seized-${index}`} className="text-sm font-medium text-black">
                          Weapon Seized/Confiscated
                        </Label>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Remarks</Label>
                        <Textarea disabled={isPreviewMode} value={weapon.remarks || ""} onChange={(e) => updateWeapon(index, "remarks", e.target.value)} className="border-gray-300" rows={2} placeholder="Additional remarks about the weapon..." />
                      </div>
                    </div>
                  </div>
                ))}

                {(!formData.weapons || formData.weapons.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No weapons added yet. Click "Add Weapon" to add weapon details.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Criminal History */}
            <Card className="border-0 bg-white shadow-sm">
              <CardHeader className="pb-3 pt-4 px-5">
                <CardTitle className="text-sm font-medium text-gray-700">Criminal History</CardTitle>
              </CardHeader>

              <CardContent className="space-y-3 px-5 pb-4">
                {/* Input Fields for New Entry */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input disabled={isPreviewMode} placeholder="Full Name" value={newCriminal.fullName} onChange={(e) => setNewCriminal({ ...newCriminal, fullName: e.target.value })} />
                  <Input disabled={isPreviewMode} placeholder="Father's Name" value={newCriminal.fatherName} onChange={(e) => setNewCriminal({ ...newCriminal, fatherName: e.target.value })} />
                  <Input disabled={isPreviewMode} placeholder="Address" value={newCriminal.address} onChange={(e) => setNewCriminal({ ...newCriminal, address: e.target.value })} />
                  <Input disabled={isPreviewMode} placeholder="Police Station" value={newCriminal.policeStation} onChange={(e) => setNewCriminal({ ...newCriminal, policeStation: e.target.value })} />
                  <Textarea disabled={isPreviewMode} placeholder="Remarks" value={newCriminal.remarks} onChange={(e) => setNewCriminal({ ...newCriminal, remarks: e.target.value })} />
                  {/* Case Details */}
                  <div className="mt-4 pt-4">
                    <h4 className="text-sm font-semibold mb-2">Add Case Detail</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input disabled={isPreviewMode} placeholder="Case Type" value={newCase.caseType} onChange={(e) => setNewCase({ ...newCase, caseType: e.target.value })} />
                      <Input disabled={isPreviewMode} placeholder="FIR Number" value={newCase.firNumber} onChange={(e) => setNewCase({ ...newCase, firNumber: e.target.value })} />
                      <Input disabled={isPreviewMode} placeholder="Case Number" value={newCase.caseNumber} onChange={(e) => setNewCase({ ...newCase, caseNumber: e.target.value })} />
                      <Input disabled={isPreviewMode} placeholder="Section of Law" value={newCase.sectionOfLaw} onChange={(e) => setNewCase({ ...newCase, sectionOfLaw: e.target.value })} />
                      <Input disabled={isPreviewMode} type="date" value={newCase.dateRegistered} onChange={(e) => setNewCase({ ...newCase, dateRegistered: e.target.value })} />
                      <Select disabled={isPreviewMode} value={newCase.status} onValueChange={(value: string) => setNewCase({ ...newCase, status: value })}>
                        <SelectTrigger className="border-gray-300 w-full">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-gray-200">
                          <SelectItem value="PENDING">Pending</SelectItem>
                          <SelectItem value="ONGOING">Ongoing</SelectItem>
                          <SelectItem value="CLOSED">Closed</SelectItem>
                          <SelectItem value="ACQUITTED">Acquitted</SelectItem>
                          <SelectItem value="CONVICTED">Convicted</SelectItem>
                        </SelectContent>
                      </Select>
                      <Textarea disabled={isPreviewMode} placeholder="Remarks" value={newCase.remarks} onChange={(e) => setNewCase({ ...newCase, remarks: e.target.value })} />
                    </div>

                    <Button
                      onClick={() => {
                        setNewCriminal((prev: any) => ({
                          ...prev,
                          cases: [...prev.cases, newCase],
                        }));
                        setNewCase({
                          caseType: "",
                          firNumber: "",
                          caseNumber: "",
                          sectionOfLaw: "",
                          dateRegistered: "",
                          status: "PENDING",
                          remarks: "",
                        });
                      }}
                      size="sm"
                      className="mt-2 bg-black text-white hover:bg-gray-800"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Case
                    </Button>

                    {/* List of added cases */}
                    {newCriminal.cases.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {newCriminal.cases.map((c: any, idx: number) => (
                          <div key={idx} className="relative p-2 border bg-gray-50 rounded text-sm text-gray-800">
                            <div>
                              <strong>Type:</strong> {c.caseType}
                            </div>
                            <div>
                              <strong>FIR:</strong> {c.firNumber}
                            </div>
                            <div>
                              <strong>Case #:</strong> {c.caseNumber}
                            </div>
                            <div>
                              <strong>Section:</strong> {c.sectionOfLaw}
                            </div>
                            <div>
                              <strong>Date:</strong> {c.dateRegistered}
                            </div>
                            <div>
                              <strong>Status:</strong> {c.status}
                            </div>
                            <div>
                              <strong>Remarks:</strong> {c.remarks}
                            </div>

                            <button onClick={() => removeCase(idx)} className="absolute top-2 right-2 text-red-600 hover:text-red-800" title="Remove case">
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Add Button */}
                <Button onClick={addCriminalHistory} size="sm" className="bg-black text-white hover:bg-gray-800 mt-2">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Record
                </Button>

                {/* List of Records */}
                {criminalHistory.length > 0 && (
                  <div className="space-y-2 mt-4">
                    {criminalHistory.map((record, index) => (
                      <div key={index} className="p-3 bg-gray-50 border rounded text-sm text-gray-800 relative">
                        <div>
                          <strong>Name:</strong> {record.fullName}
                        </div>
                        <div>
                          <strong>Father:</strong> {record.fatherName}
                        </div>
                        <div>
                          <strong>Address:</strong> {record.address}
                        </div>
                        <div>
                          <strong>PS:</strong> {record.policeStation}
                        </div>
                        <div>
                          <strong>Remarks:</strong> {record.remarks}
                        </div>
                        <div>
                          <strong>Cases:</strong> {JSON.stringify(record.cases)}
                        </div>

                        <Button onClick={() => removeCriminalHistory(index)} size="sm" variant="ghost" className="absolute top-2 right-2 text-red-600 hover:bg-red-100">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Report Information */}
            <Card className="border-0 bg-white shadow-sm">
              <CardHeader className="pb-3 pt-4 px-5">
                <CardTitle className="text-sm font-medium text-gray-700">Report Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 px-5 pb-4">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-600">Report Printed On</Label>
                  <Input disabled={isPreviewMode} type="datetime-local" value={formData.reportPrintedOn || ""} onChange={(e) => handleInputChange("reportPrintedOn", e.target.value)} className={showErrors && getFieldError("reportPrintedOn") ? "border-red-500" : "border-gray-300"} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Printed By - Name</Label>
                    <Input disabled={isPreviewMode} value={formData.reportPrintedByName || ""} onChange={(e) => handleInputChange("reportPrintedByName", e.target.value)} className="border-gray-300" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Printed By - Rank</Label>
                    <Input disabled={isPreviewMode} value={formData.reportPrintedByRank || ""} onChange={(e) => handleInputChange("reportPrintedByRank", e.target.value)} className="border-gray-300" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Signature - Name</Label>
                    <Input disabled={isPreviewMode} value={formData.signatureName || ""} onChange={(e) => handleInputChange("signatureName", e.target.value)} className="border-gray-300" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Signature - Rank</Label>
                    <Input disabled={isPreviewMode} value={formData.signatureRank || ""} onChange={(e) => handleInputChange("signatureRank", e.target.value)} className="border-gray-300" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-600">Signature - ID No</Label>
                    <Input disabled={isPreviewMode} value={formData.signatureIdNo || ""} onChange={(e) => handleInputChange("signatureIdNo", e.target.value)} className="border-gray-300" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        <div className="flex-shrink-0 flex justify-end space-x-2 pt-4 border-t border-gray-200 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-300">
            Cancel
          </Button>

          {isPreviewMode ? (
            <>
              <Button variant="outline" onClick={() => setIsPreviewMode(false)}>
                Edit Again
              </Button>
              <Button onClick={handleSubmit} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Confirm & Submit
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setShowErrors(true);
                if (validateForm()) {
                  setTimeout(() => {
                    const scrollable = document.querySelector("[data-radix-scroll-area-viewport]") as HTMLElement;
                    if (scrollable) {
                      scrollable.scrollTo({ top: 0, behavior: "smooth" });
                    } else {
                      console.warn("Scroll viewport not found");
                    }
                  }, 200);

                  setIsPreviewMode(true);
                } else {
                  showValidationToast(validationErrors);
                }
              }}
              className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
            >
              Preview Before Submit
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
